import React, { useState } from "react";
import { confirmResetPassword } from "aws-amplify/auth";
import { useSearchParams, useNavigate } from "react-router-dom";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const emailFromURL = searchParams.get("email") || "";
  const [email] = useState(emailFromURL);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Mask email for display
  const maskEmail = (email) => {
    if (!email) return "";
    const [local, domain] = email.split("@");
    if (!local || !domain) return email;
    const maskedLocal = local[0] + "***";
    const maskedDomain = domain[0] + "***";
    return `${maskedLocal}@${maskedDomain}`;
  };

  const isPasswordValid = (pwd) =>
  pwd.length >= 8 &&
  /[A-Z]/.test(pwd) &&
  /[a-z]/.test(pwd) &&
  /[0-9]/.test(pwd) &&
  /[!@#$%^&*(),.?":{}|<>]/.test(pwd);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErr("Passwords do not match.");
      setLoading(false);
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setErr("Password must be at least 8 characters long.");
      setLoading(false);
      return;
    }

    try {
      await confirmResetPassword({
        username: email.toLowerCase(),
        confirmationCode: code,
        newPassword: newPassword,
      });

      setSuccess(true);

      // Redirect to sign-in after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("Confirm reset password error:", error);

      if (error.name === "CodeMismatchException") {
        setErr("Invalid verification code. Please check and try again.");
      } else if (error.name === "ExpiredCodeException") {
        setErr("Verification code has expired. Please request a new one.");
      } else if (error.name === "InvalidPasswordException") {
        setErr("Password does not meet requirements. Use at least 8 characters with uppercase, lowercase, and numbers.");
      } else if (error.name === "LimitExceededException") {
        setErr("Too many attempts. Please try again later.");
      } else {
        setErr(error.message || "Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <img
            src="infauxtech_logo_rectangle.png"
            alt="Media Forensics Hub"
            className="w-32 mx-auto mb-4"
          />
          <div className="mb-4">
            <svg
              className="w-16 h-16 text-green-500 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-purple-800 mb-2">
            Password Changed!
          </h2>
          <p className="text-gray-600 mb-4">
            Your password has been successfully reset.
          </p>
          <p className="text-gray-500 text-sm">
            Redirecting to sign in...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center mb-6">
          <img
            src="infauxtech_logo_rectangle.png"
            alt="Media Forensics Hub"
            className="w-32 mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-purple-800">
            Reset password
          </h2>

          {/* Email icon and message */}
          <div className="mt-4 mb-2">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-2">
              <svg
                className="w-6 h-6 text-purple-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>

          <p className="text-gray-600 text-sm">
            We have sent a password reset code in an Email message to{" "}
            <span className="font-semibold">{maskEmail(email)}</span>.
            Enter your code and your new password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-left text-gray-700 font-medium mb-1">
              Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
              placeholder="Enter verification code"
              required
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-left text-gray-700 font-medium mb-1">
                New password
            </label>
            <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                placeholder="Enter new password"
                required
                autoComplete="new-password"
            />

            {/* Password strength rules */}
            <ul className="mt-2 text-xs text-gray-600 space-y-1">
                <li className={newPassword.length >= 8 ? "text-green-600" : "text-gray-500"}>
                • At least 8 characters
                </li>
                <li className={/[A-Z]/.test(newPassword) ? "text-green-600" : "text-gray-500"}>
                • One uppercase letter (A–Z)
                </li>
                <li className={/[a-z]/.test(newPassword) ? "text-green-600" : "text-gray-500"}>
                • One lowercase letter (a–z)
                </li>
                <li className={/[0-9]/.test(newPassword) ? "text-green-600" : "text-gray-500"}>
                • One number (0–9)
                </li>
                <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? "text-green-600" : "text-gray-500"}>
                • One special character (!@#$%^&*)
                </li>
            </ul>
            </div>


          <div>
            <label className="block text-left text-gray-700 font-medium mb-1">
              Confirm new password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
              placeholder="Confirm new password"
              required
              autoComplete="new-password"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="showPassword"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              className="mr-2 h-4 w-4 text-purple-700 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="showPassword" className="text-sm text-gray-700">
              Show password
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !isPasswordValid(newPassword)}
            className="w-full bg-purple-700 text-white font-semibold py-2 rounded-lg hover:bg-purple-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
            {loading ? "Changing password..." : "Change password"}
        </button>

        </form>

        {err && (
          <p className="mt-4 text-red-600 text-center font-medium text-sm">
            {err}
          </p>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/forgot-password")}
            className="text-purple-700 font-semibold hover:underline text-sm"
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
}
